/**
 * RoadmapPage — public roadmap timeline (live API-driven).
 *
 * UX corrections shipped in this iteration (owner directive 2026-02-02):
 *   - Service filter (All / JapanETCcard / JapanTollReceipts /
 *     JapanChangeMoney / NoJo Enterprise) — combines with status filter
 *   - Status filter defaults to `in_progress` on first load (with
 *     intelligent fallback chain: in_progress → in_testing → planned →
 *     completed) so visitors see active work first
 *   - Pagination 30 items per page with First / Prev / numbered / Next /
 *     Last controls and query-param persistence
 *   - Bottom safety section redesigned: replaces the two big dark boxes
 *     ("Building This Together" + "Public Information Only") with one
 *     compact card with tri-color top stripe
 *   - Bilingual JST-safe date formatting on milestone cards
 *
 * Source of truth: live `/api/integrations/ecosystem/roadmap`. Adapter
 * filters publicDisplayApproved=true upstream.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  CalendarClock,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  FlaskConical,
  Loader2,
  Rocket,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import WhatsNewRoadmapTabs from "@/components/WhatsNewRoadmapTabs";
import Reveal from "@/components/motion/Reveal";
import Tooltip from "@/components/Tooltip";
import PageSideAura from "@/components/motion/PageSideAura";
import SourceFreshnessBadge from "@/components/SourceFreshnessBadge";
import SEO, { PAGE_SEO } from "@/components/SEO";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";
import FloatingPager from "@/components/FloatingPager";
import { useEcosystemDataset } from "@/lib/useEcosystemData";
import { formatPublicDate } from "@/lib/formatPublicDate";

const PAGE_SIZE = 30;

const SERVICE_LABELS = {
  all: { en: "All services", ja: "すべて" },
  japanetccard: { en: "JapanETCcard", ja: "JapanETCcard" },
  japantollreceipts: { en: "JapanTollReceipts", ja: "JapanTollReceipts" },
  japanchangemoney: { en: "JapanChangeMoney", ja: "JapanChangeMoney" },
  "nojo-enterprise": { en: "NoJo Enterprise", ja: "NoJo Enterprise" },
};

const SERVICE_HEX = {
  japanetccard: "#9333EA",        // vibrant purple
  japantollreceipts: "#16A34A",   // vibrant green
  japanchangemoney: "#2563EB",    // vibrant blue
  "nojo-enterprise": "#4F46E5",   // indigo (bluish-purple) — parent
};

const STATUS_ICON = {
  completed: CheckCircle2,
  in_testing: FlaskConical,
  in_progress: Loader2,
  planned: CalendarClock,
  launch: Rocket,
};

const STATUS_PILL_CLASS = {
  completed: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  in_testing: "bg-sky-50 text-sky-700 ring-sky-200",
  in_progress: "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200",
  planned: "bg-amber-50 text-amber-700 ring-amber-200",
  launch: "bg-rose-50 text-rose-700 ring-rose-200",
};

const STATUS_FILTER_ORDER = ["in_progress", "in_testing", "planned", "completed", "launch"];

function pickLang(field, lang) {
  if (!field) return "";
  if (typeof field === "string") return field;
  return field[lang] || field.en || field.ja || "";
}

function daysUntilLaunch(launchDate) {
  if (!launchDate) return 0;
  const target = new Date(launchDate + "T00:00:00+09:00");
  const today = new Date();
  return Math.max(0, Math.ceil((target - today) / 86400000));
}

function MilestoneCard({ m, lang, STATUS_META }) {
  const StatusIcon = STATUS_ICON[m.status] || CalendarClock;
  const statusMeta = STATUS_META[m.status] || STATUS_META.planned;
  const serviceId = m.category || m._serviceId || "nojo-enterprise";
  const serviceLabel = SERVICE_LABELS[serviceId] || SERVICE_LABELS["nojo-enterprise"];
  const serviceHex = SERVICE_HEX[serviceId] || SERVICE_HEX["nojo-enterprise"];
  const isLaunch = m.status === "launch";
  const displayDate = m._sortDate ? formatPublicDate(m._sortDate, lang) : "";

  return (
    <div
      data-testid={`roadmap-milestone-${serviceId}-${m.status}`}
      className="relative pl-8 sm:pl-10 pb-8 last:pb-0"
    >
      <div
        className={`absolute left-0 top-1.5 flex items-center justify-center w-7 h-7 rounded-full ring-4 ring-white ${
          isLaunch ? "bg-rose-600" : "bg-white border-2"
        }`}
        style={!isLaunch ? { borderColor: statusMeta.dot } : {}}
      >
        <StatusIcon
          className={`w-3.5 h-3.5 ${m.status === "in_progress" ? "animate-spin" : ""} ${isLaunch ? "text-white" : ""}`}
          style={!isLaunch ? { color: statusMeta.dot } : {}}
        />
      </div>
      <div
        className={`rounded-xl border bg-white p-4 sm:p-5 ${
          isLaunch
            ? "bg-gradient-to-br from-rose-50 via-fuchsia-50 to-white border-rose-200"
            : "border-slate-200"
        }`}
        data-testid={`roadmap-milestone-card-${m.id}`}
      >
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-[11px] font-semibold rounded-full ring-1 ${
              STATUS_PILL_CLASS[m.status] || STATUS_PILL_CLASS.planned
            }`}
          >
            <StatusIcon className={`w-3 h-3 ${m.status === "in_progress" ? "animate-spin" : ""}`} />
            {pickLang(statusMeta, lang)}
          </span>
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[11px] font-semibold rounded-full"
            style={{ color: serviceHex, backgroundColor: `${serviceHex}10`, border: `1px solid ${serviceHex}33` }}
          >
            <span aria-hidden className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: serviceHex }} />
            {pickLang(serviceLabel, lang)}
          </span>
          {displayDate && (
            <span className="text-[10.5px] tracking-[0.14em] font-mono text-slate-400 ml-auto">
              {displayDate}
            </span>
          )}
        </div>
        <h2
          className={`font-semibold leading-snug text-slate-900 ${
            isLaunch ? "text-lg sm:text-xl" : "text-base sm:text-lg"
          }`}
        >
          {pickLang(m.title, lang)}
        </h2>
        {pickLang(m.desc, lang) && (
          <p className="mt-1.5 text-sm text-slate-600 leading-relaxed line-clamp-3">
            {pickLang(m.desc, lang)}
          </p>
        )}
      </div>
    </div>
  );
}

function Pagination({ page, pageCount, onChange, isJa, total, shown, filtered }) {
  if (pageCount <= 1) return null;
  const pages = [];
  const start = Math.max(1, page - 2);
  const end = Math.min(pageCount, start + 4);
  for (let p = start; p <= end; p += 1) pages.push(p);

  const Btn = ({ to, label, ariaLabel, testId, disabled, active, children }) => (
    <button
      type="button"
      onClick={() => onChange(to)}
      disabled={disabled}
      aria-label={ariaLabel || label}
      data-testid={testId}
      className={[
        "inline-flex items-center justify-center min-w-[36px] h-9 px-2.5 rounded-full text-xs font-semibold transition-colors",
        active
          ? "bg-[#9333EA] text-white"
          : disabled
          ? "bg-white text-slate-300 cursor-not-allowed border border-slate-200"
          : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50",
      ].join(" ")}
    >
      {children || label}
    </button>
  );

  return (
    <nav
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-12"
      data-testid="roadmap-pagination"
      aria-label={isJa ? "ページ送り" : "Pagination"}
    >
      <p className="text-xs text-slate-500" data-testid="roadmap-pagination-summary">
        {filtered
          ? isJa
            ? `該当${total}件中 ${(page - 1) * PAGE_SIZE + 1}〜${(page - 1) * PAGE_SIZE + shown}件を表示`
            : `Showing ${(page - 1) * PAGE_SIZE + 1}–${(page - 1) * PAGE_SIZE + shown} of ${total} matching items`
          : isJa
          ? `${total}件中 ${(page - 1) * PAGE_SIZE + 1}〜${(page - 1) * PAGE_SIZE + shown}件を表示`
          : `Showing ${(page - 1) * PAGE_SIZE + 1}–${(page - 1) * PAGE_SIZE + shown} of ${total} roadmap items`}
      </p>
      <div className="flex items-center gap-1.5">
        <Btn to={1} testId="rm-page-first" disabled={page === 1} label={isJa ? "最初" : "First"}>
          <ChevronsLeft className="w-3.5 h-3.5" />
        </Btn>
        <Btn to={page - 1} testId="rm-page-prev" disabled={page === 1} label={isJa ? "前へ" : "Previous"}>
          <ChevronLeft className="w-3.5 h-3.5" />
        </Btn>
        {pages.map((p) => (
          <Btn key={p} to={p} active={p === page} testId={`rm-page-${p}`} label={String(p)} />
        ))}
        <Btn to={page + 1} testId="rm-page-next" disabled={page === pageCount} label={isJa ? "次へ" : "Next"}>
          <ChevronRight className="w-3.5 h-3.5" />
        </Btn>
        <Btn to={pageCount} testId="rm-page-last" disabled={page === pageCount} label={isJa ? "最後" : "Last"}>
          <ChevronsRight className="w-3.5 h-3.5" />
        </Btn>
      </div>
    </nav>
  );
}

export default function RoadmapPage({ lang }) {
  const isJa = lang === "ja";
  const dataset = useEcosystemDataset(lang);
  const location = useLocation();
  const navigate = useNavigate();

  // Read filter + page from query params for shareable URLs.
  const queryParams = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return {
      service: sp.get("service") || "all",
      status: sp.get("status") || "",
      page: Math.max(1, parseInt(sp.get("page") || "1", 10) || 1),
    };
  }, [location.search]);

  // Title + meta are owned by the <SEO> component — Helmet handles updates.

  const { MILESTONES: ALL_MILESTONES, STATUS_META, LAUNCH_DATE } = dataset;

  // Owner directive (May 2026): NoJo Enterprise's corporate roadmap is
  // complete — going forward the Roadmap page focuses only on the
  // operated services (JEC / JTR / JCM). Hide NoJo Enterprise items
  // from the entire page so the totals, filters and visible cards all
  // reflect the same scope.
  const MILESTONES = useMemo(
    () => ALL_MILESTONES.filter((m) => (m.category || m._serviceId) !== "nojo-enterprise"),
    [ALL_MILESTONES],
  );

  const services = useMemo(() => {
    const set = new Set();
    MILESTONES.forEach((m) => set.add(m.category || m._serviceId || "japanetccard"));
    // Canonical service order owner-locked (post-NoJo-completion):
    // JEC → JTR → JCM. Append any unknown ids at the end.
    const CANONICAL = ["japanetccard", "japantollreceipts", "japanchangemoney"];
    const ordered = CANONICAL.filter((s) => set.has(s));
    Array.from(set).forEach((s) => {
      if (!ordered.includes(s)) ordered.push(s);
    });
    return ["all", ...ordered];
  }, [MILESTONES]);

  // Counts by status, scoped to current service filter (so chips reflect reality).
  const statusCounts = useMemo(() => {
    const base = MILESTONES.filter((m) =>
      queryParams.service === "all" ? true : (m.category || m._serviceId) === queryParams.service,
    );
    const counts = { all: base.length };
    STATUS_FILTER_ORDER.forEach((s) => { counts[s] = 0; });
    base.forEach((m) => {
      if (counts[m.status] !== undefined) counts[m.status] += 1;
    });
    return counts;
  }, [MILESTONES, queryParams.service]);

  // Default-to-in-progress logic with intelligent fallback.
  const effectiveStatus = useMemo(() => {
    if (queryParams.status) return queryParams.status;
    if (!dataset.loaded) return "in_progress";
    // Pick the first status in priority order that has ≥1 item for this service.
    for (const s of STATUS_FILTER_ORDER) {
      if (statusCounts[s] > 0) return s;
    }
    return "all";
  }, [queryParams.status, statusCounts, dataset.loaded]);

  const statusWasFallback =
    !queryParams.status && dataset.loaded && effectiveStatus !== "in_progress" && effectiveStatus !== "all";

  const setQueryParams = (next) => {
    const sp = new URLSearchParams(location.search);
    Object.entries(next).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "" || (k === "service" && v === "all") || (k === "page" && v === 1)) {
        sp.delete(k);
      } else {
        sp.set(k, String(v));
      }
    });
    navigate({ pathname: location.pathname, search: sp.toString() }, { replace: false });
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const setService = (svc) => setQueryParams({ service: svc, page: 1 });
  // FIX (2026-05-19): passing "" used to clear the URL param, which then
  // triggered the fallback chain in `effectiveStatus` and forced
  // `in_progress` back on — making the "All" button impossible to select.
  // We now pass the literal value through; "all" is kept in the URL and
  // `effectiveStatus` treats it as "show every status".
  const setStatus = (st) => setQueryParams({ status: st, page: 1 });
  const setPage = (p) => setQueryParams({ page: p });

  // Apply filters
  const filtered = useMemo(() => {
    return MILESTONES.filter((m) => {
      const svc = m.category || m._serviceId || "nojo-enterprise";
      if (queryParams.service !== "all" && svc !== queryParams.service) return false;
      if (effectiveStatus !== "all" && m.status !== effectiveStatus) return false;
      return true;
    });
  }, [MILESTONES, queryParams.service, effectiveStatus]);

  // Sort soonest-upcoming first (ascending by date). A roadmap is
  // forward-looking, so the items the team is actively working on next
  // should sit at the top of the list, with far-future quarters at the
  // bottom. Owner directive 2026-05-19.
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const ad = new Date(a._sortDate || 0).getTime();
      const bd = new Date(b._sortDate || 0).getTime();
      return ad - bd;
    });
  }, [filtered]);

  const total = sorted.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const page = Math.min(queryParams.page, pageCount);
  const visible = useMemo(() => {
    const startIdx = (page - 1) * PAGE_SIZE;
    return sorted.slice(startIdx, startIdx + PAGE_SIZE);
  }, [sorted, page]);

  const stats = useMemo(() => {
    const counts = { completed: 0, in_testing: 0, in_progress: 0, planned: 0, launch: 0 };
    MILESTONES.forEach((m) => {
      if (counts[m.status] !== undefined) counts[m.status] += 1;
    });
    const denom = Math.max(1, MILESTONES.length);
    const pct = Math.round((counts.completed / denom) * 100);
    return { ...counts, total: MILESTONES.length, pct };
  }, [MILESTONES]);

  const days = daysUntilLaunch(LAUNCH_DATE);

  const t = isJa
    ? {
        eyebrow: "公開ロードマップ",
        h1: "これからの取り組み",
        sub: "NoJo Enterpriseと運営する各サービスの、公開が承認された今後の計画とロードマップ項目です。",
        daysLabel: "ローンチまで",
        progressLabel: "全体進捗",
        statusFilterLabel: "状況で絞り込み",
        serviceFilterLabel: "サイトで絞り込み",
        filterAll: "すべて",
        emptyState: "条件に一致するロードマップ項目はありません。フィルターをリセットしてください。",
        statusFallback: "現在表示できる進行中に近い項目を表示しています。",
        safetyTitle: "公開ロードマップと、保護される情報",
        safetyDesc:
          "このページには、公開が承認されたロードマップ項目のみを表示しています。内部計画、個人情報、機密性の高い運用情報、セキュリティ上の詳細は表示されません。",
        safetyContact: "ロードマップ項目についてご質問がある場合は、NoJo Enterpriseまでお問い合わせください。",
        ctaBtn: "お問い合わせ",
      }
    : {
        eyebrow: "Public roadmap",
        h1: "Where we're going",
        sub: "Approved forward plans and roadmap items from NoJo Enterprise and the services it operates.",
        daysLabel: "days to launch",
        progressLabel: "overall progress",
        statusFilterLabel: "Filter by Status",
        serviceFilterLabel: "Filter by Site",
        filterAll: "All",
        emptyState: "No roadmap items match the current filters. Try clearing them.",
        statusFallback: "Showing the nearest active roadmap status.",
        safetyTitle: "Public roadmap, protected details.",
        safetyDesc:
          "This page shows approved public roadmap items only. Internal planning, private customer data, sensitive operations, and security details are not displayed.",
        safetyContact: "Have a question about a roadmap item? Contact the NoJo team.",
        ctaBtn: "Contact us",
      };

  return (
    <div className="bg-gradient-to-b from-slate-50 via-white to-white min-h-screen" data-testid="roadmap-page">
      <SEO {...PAGE_SEO.roadmap[lang]} path={`/${lang}/roadmap`} lang={lang} />
      <JsonLd
        data={breadcrumbSchema([
          { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
          { name: isJa ? "ロードマップ" : "Roadmap", url: `/${lang}/roadmap` },
        ])}
      />
      <WhatsNewRoadmapTabs />
      <div className="pb-20">
        {/* HERO */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.05] pointer-events-none" aria-hidden>
            <div className="absolute -top-32 -left-32 w-[40rem] h-[40rem] rounded-full bg-[#0E5FB0] blur-3xl" />
            <div className="absolute -top-20 right-0 w-[36rem] h-[36rem] rounded-full bg-[#5B2E83] blur-3xl" />
          </div>
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-5 pb-4 sm:pt-7 sm:pb-5">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8 items-start">
              <div className="lg:col-span-7">
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-amber-100 text-amber-800 ring-1 ring-amber-200"
                  data-testid="roadmap-eyebrow"
                >
                  <Sparkles className="w-3 h-3" />
                  {t.eyebrow}
                </span>
                <h1
                  className={[
                    isJa ? "font-ja" : "font-display",
                    "mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900",
                  ].join(" ")}
                  data-testid="roadmap-h1"
                >
                  {t.h1}
                </h1>
                <p className="mt-1.5 text-sm text-slate-600 max-w-2xl leading-relaxed">{t.sub}</p>
                <div className="mt-3 h-1.5 rounded-full bg-slate-200 overflow-hidden" aria-hidden>
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500 via-sky-500 to-fuchsia-500 transition-all"
                    style={{ width: `${stats.pct}%` }}
                    data-testid="roadmap-progress-bar"
                  />
                </div>
                {dataset.stale && (
                  <div className="mt-1.5 text-[10.5px] tracking-[0.18em] font-mono uppercase text-slate-400">
                    {isJa ? "キャッシュ表示中" : "Showing cached snapshot"}
                  </div>
                )}
                <SourceFreshnessBadge
                  sourceFreshness={dataset.sourceFreshness}
                  lang={lang}
                  className="mt-2"
                />
              </div>

              <div className="lg:col-span-5">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="rounded-lg bg-white ring-1 ring-slate-200 px-3 py-2" data-testid="roadmap-stat-days">
                    <div className="text-xl font-bold text-slate-900 leading-tight">{days}</div>
                    <div className="text-[10px] text-slate-500 leading-tight">{t.daysLabel}</div>
                  </div>
                  <div className="rounded-lg bg-white ring-1 ring-slate-200 px-3 py-2" data-testid="roadmap-stat-progress">
                    <div className="text-xl font-bold text-emerald-600 leading-tight">{stats.pct}%</div>
                    <div className="text-[10px] text-slate-500 leading-tight">{t.progressLabel}</div>
                  </div>
                  <div className="rounded-lg bg-emerald-50 ring-1 ring-emerald-200 px-3 py-2" data-testid="roadmap-stat-completed">
                    <div className="text-xl font-bold text-emerald-700 leading-tight">{stats.completed}</div>
                    <div className="text-[10px] text-emerald-700/80 leading-tight">{pickLang(STATUS_META.completed, lang)}</div>
                  </div>
                  <div className="rounded-lg bg-sky-50 ring-1 ring-sky-200 px-3 py-2" data-testid="roadmap-stat-testing">
                    <div className="text-xl font-bold text-sky-700 leading-tight">{stats.in_testing}</div>
                    <div className="text-[10px] text-sky-700/80 leading-tight">{pickLang(STATUS_META.in_testing, lang)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            MIDDLE CONTENT — wrapped with a subtle side aura sitting in
            the page gutters so the purple/blue glow reads on the right
            and left of the central reading column, NOT over the hero
            above or the safety footer below.
            ═══════════════════════════════════════════════════════════ */}
        <div className="relative isolate" data-testid="roadmap-middle">
          <PageSideAura accent="#5B2E83" accent2="#0E5FB0" testId="roadmap-side-aura" />

        {/* FILTER BAR — two rows: site then status (clearer separation) */}
        <section className="bg-white/90 border-y border-slate-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 space-y-4">
            {/* Site filter — wrap on mobile so all chips stay visible
                (owner fix 2026-02-24, same root cause as WhatsNew). */}
            <div
              role="group"
              aria-labelledby="roadmap-service-filter-label"
              className="flex flex-wrap items-center gap-2 sm:gap-3 sm:flex-nowrap sm:overflow-x-auto overflow-y-visible py-2"
            >
              <span
                id="roadmap-service-filter-label"
                className="text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap w-full sm:w-auto sm:min-w-[130px]"
              >
                {t.serviceFilterLabel}
              </span>
              {services.map((svc) => {
                const isActive = queryParams.service === svc;
                const label = pickLang(SERVICE_LABELS[svc] || SERVICE_LABELS["nojo-enterprise"], lang);
                const hex = svc === "all" ? "#663399" : (SERVICE_HEX[svc] || "#663399");
                // Soft-tint colors derived per service color (vivid family).
                const softMap = {
                  "#663399": { soft: "#F3E8FF", ring: "#E9D5FF" }, // logo purple
                  "#9333EA": { soft: "#F3E8FF", ring: "#E9D5FF" }, // purple
                  "#16A34A": { soft: "#DCFCE7", ring: "#BBF7D0" }, // green
                  "#2563EB": { soft: "#DBEAFE", ring: "#BFDBFE" }, // blue
                  "#4F46E5": { soft: "#E0E7FF", ring: "#C7D2FE" }, // indigo
                };
                const tint = softMap[hex] || softMap["#663399"];
                const tipAccent = svc === "all"
                  ? "purple"
                  : svc === "japanetccard" ? "purple"
                  : svc === "japantollreceipts" ? "green"
                  : svc === "japanchangemoney" ? "blue"
                  : "indigo";
                const tipText = svc === "all"
                  ? (lang === "ja" ? "全てのサイトを表示" : "Show all sites")
                  : (lang === "ja" ? `${label}に絞り込み` : `Filter to ${label}`);
                return (
                  <Tooltip
                    key={svc}
                    label={tipText}
                    accent={tipAccent}
                    placement="top"
                    testId={`roadmap-service-filter-${svc}-tip`}
                  >
                  <button
                    type="button"
                    onClick={() => setService(svc)}
                    data-testid={`roadmap-service-filter-${svc}`}
                    aria-pressed={isActive}
                    className={`shrink-0 px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap transition-all duration-200 inline-flex items-center gap-1.5 ${
                      isActive
                        ? "hover:-translate-y-0.5"
                        : "hover:-translate-y-0.5 hover:shadow-md hover:scale-[1.03]"
                    } active:scale-95`}
                    style={
                      isActive
                        ? {
                            backgroundColor: hex,
                            color: "#ffffff",
                            border: `1px solid ${hex}`,
                            boxShadow: `0 6px 18px -8px ${hex}99`,
                          }
                        : {
                            backgroundColor: tint.soft,
                            color: hex,
                            border: `1px solid ${tint.ring}`,
                          }
                    }
                  >
                    {svc !== "all" && (
                      <span aria-hidden className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: isActive ? "white" : hex }} />
                    )}
                    {svc === "all" ? t.filterAll : label}
                  </button>
                  </Tooltip>
                );
              })}
            </div>
            {/* Status filter — distinct semantic palette (no service brand colors).
                Wrap on mobile so all status chips stay visible. */}
            <div
              role="group"
              aria-labelledby="roadmap-status-filter-label"
              className="flex flex-wrap items-center gap-2 sm:gap-3 sm:flex-nowrap sm:overflow-x-auto overflow-y-visible py-2 sm:pr-2"
            >
              <span
                id="roadmap-status-filter-label"
                className="text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap w-full sm:w-auto sm:min-w-[130px]"
              >
                {t.statusFilterLabel}
              </span>
              {["all", ...STATUS_FILTER_ORDER].map((opt) => {
                const isActive = effectiveStatus === opt || (opt === "all" && effectiveStatus === "all");
                const label = opt === "all" ? t.filterAll : pickLang(STATUS_META[opt], lang);
                const cnt = statusCounts[opt] || 0;
                // Status pill palette — semantically distinct from the
                // service brand colors. Owner-locked mapping:
                //   all → purple, in_progress → fuchsia (pink),
                //   in_testing → sky (blue), planned → amber (yellow),
                //   completed → emerald (green), launch → rose (red).
                const STATUS_ACCENT = {
                  all:         { hex: "#9333EA", soft: "#F3E8FF", ring: "#E9D5FF" }, // purple
                  in_progress: { hex: "#D946EF", soft: "#FAE8FF", ring: "#F5D0FE" }, // fuchsia/pink
                  in_testing:  { hex: "#0EA5E9", soft: "#E0F2FE", ring: "#BAE6FD" }, // sky/blue
                  planned:     { hex: "#F59E0B", soft: "#FEF3C7", ring: "#FDE68A" }, // amber/yellow
                  completed:   { hex: "#10B981", soft: "#D1FAE5", ring: "#A7F3D0" }, // emerald/green
                  launch:      { hex: "#E11D48", soft: "#FFE4E6", ring: "#FECDD3" }, // rose/red
                };
                const a = STATUS_ACCENT[opt] || STATUS_ACCENT.all;
                const tipAccentMap = {
                  all: "purple",
                  in_progress: "pink",
                  in_testing: "sky",
                  planned: "amber",
                  completed: "green",
                  launch: "rose",
                };
                const tipAccent = tipAccentMap[opt] || "purple";
                const tipText =
                  opt === "all"
                    ? (lang === "ja" ? `すべての項目 · ${cnt}件` : `Show all items · ${cnt}`)
                    : (lang === "ja"
                        ? `${label}のみ表示 · ${cnt}件`
                        : `Show ${label.toLowerCase()} only · ${cnt}`);
                return (
                  <Tooltip
                    key={opt}
                    label={tipText}
                    accent={tipAccent}
                    placement="top"
                    testId={`roadmap-filter-${opt}-tip`}
                  >
                  <button
                    type="button"
                    onClick={() => setStatus(opt)}
                    data-testid={`roadmap-filter-${opt}`}
                    aria-pressed={isActive}
                    className={`shrink-0 px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap transition-all duration-200 ring-1 inline-flex items-center gap-1.5 ${
                      isActive
                        ? "hover:-translate-y-0.5"
                        : "hover:-translate-y-0.5 hover:shadow-md hover:scale-[1.03]"
                    } active:scale-95`}
                    style={
                      isActive
                        ? {
                            backgroundColor: a.hex,
                            color: "#ffffff",
                            border: `1px solid ${a.hex}`,
                            boxShadow: `0 6px 18px -8px ${a.hex}99`,
                          }
                        : {
                            backgroundColor: a.soft,
                            color: a.hex,
                            border: `1px solid ${a.ring}`,
                          }
                    }
                  >
                    {label}
                    <span
                      className="text-[10px] tracking-tight font-mono px-1 rounded-full"
                      style={
                        isActive
                          ? { backgroundColor: "rgba(255,255,255,0.22)", color: "#ffffff" }
                          : { backgroundColor: "rgba(255,255,255,0.65)", color: a.hex }
                      }
                    >
                      {cnt}
                    </span>
                  </button>
                  </Tooltip>
                );
              })}
            </div>
            {statusWasFallback && (
              <p className="text-[11px] text-slate-500 italic" data-testid="roadmap-fallback-note">
                {t.statusFallback}
              </p>
            )}
          </div>
        </section>

        {/* TIMELINE — 30 per page, soonest-upcoming first */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-10" data-testid="roadmap-timeline">
          <div className="relative">
            <div className="absolute left-3.5 sm:left-[18px] top-0 bottom-0 w-px bg-slate-200" />
            {visible.map((m, i) => (
              <Reveal as="div" variant="up" delay={i * 18} key={m.id || i}>
                <MilestoneCard m={m} lang={lang} STATUS_META={STATUS_META} />
              </Reveal>
            ))}
          </div>

          {dataset.loaded && visible.length === 0 && (
            <p className="text-[13px] text-slate-500 text-center py-16" data-testid="roadmap-empty">
              {t.emptyState}
            </p>
          )}

          <Pagination
            page={page}
            pageCount={pageCount}
            onChange={setPage}
            isJa={isJa}
            total={total}
            shown={visible.length}
            filtered={queryParams.service !== "all" || effectiveStatus !== "all"}
          />
          {pageCount > 1 && (
            <FloatingPager
              onPrev={() => setPage(page - 1)}
              onNext={() => setPage(page + 1)}
              atFirst={page <= 1}
              atLast={page >= pageCount}
              isJa={isJa}
              accent="#9333EA"
              testIdPrefix="roadmap-floating-pager"
            />
          )}
        </section>
        </div>
        {/* ═════════ END MIDDLE CONTENT (side aura wrapper closes) ════════ */}

        {/* COMPACT SAFETY FOOTER — replaces "Building This Together" + dark box */}
        <section className="mt-14" data-testid="roadmap-safety-footer">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="relative rounded-xl bg-white ring-1 ring-slate-200 px-5 py-4 overflow-hidden">
              <span
                aria-hidden
                className="absolute top-0 left-0 right-0 h-1"
                style={{ background: "linear-gradient(90deg, #5B2E83 0%, #2F8F3C 50%, #0E5FB0 100%)" }}
              />
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-start gap-2.5">
                  <ShieldCheck className="h-4 w-4 text-slate-700 mt-0.5 shrink-0" strokeWidth={2} />
                  <div>
                    <h2 className="text-sm font-semibold text-slate-900 leading-snug">{t.safetyTitle}</h2>
                    <p className="text-[12.5px] text-slate-600 leading-relaxed mt-0.5">{t.safetyDesc}</p>
                    <p className="text-[12.5px] text-slate-500 leading-relaxed mt-1">{t.safetyContact}</p>
                  </div>
                </div>
                <Link
                  to={`/${lang}/contact`}
                  data-testid="roadmap-cta-contact"
                  className="ml-auto shrink-0 inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-[#9333EA] text-white text-xs font-semibold hover:bg-[#7C3AED] transition-colors whitespace-nowrap"
                >
                  {t.ctaBtn}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
